import { makeAutoObservable, runInAction } from 'mobx';
import { ProgramParams, createProgramParams } from './ProgramParams';
import { HttpBackend } from '../../../Services/Http/HttpBackend';
import { WorkoutLogHeader, createWorkoutLogHeader } from '../../WorkoutLog/WorkoutLogHeader';
import dayjs from '../../../Utils/dayjs';
import { CoachProgramType } from './CoachProgramType';
import { UnitSchedule, createUnitSchedule } from './UnitSchedule';
import { CoachProgram } from './CoachProgram';
import { CoachWorkout, createCoachWorkout } from './Workout/CoachWorkout';
import { WorkoutLog } from '../../WorkoutLog/WorkoutLog';

export type MicroCycleDayJson = {
  id: string;
  athleteSuperMacroId: string;
  mesoCycleId: string;
  microCycleId: string;
  workoutDayTemplateId: string;
  params: ProgramParams;
  coachWorkoutId: string;
  workoutLogId?: string;
  skipped: boolean;
  type: CoachProgramType;
  isLastMicroCycleDay: boolean;
};

export type MicroCycleDay = {
  id: string;
  athleteSuperMacroId: string;
  mesoCycleId: string;
  microCycleId: string;
  workoutDayTemplateId: string;
  params: ProgramParams;
  coachWorkoutId: string;
  workout?: CoachWorkout;
  workoutLogId?: string;
  workoutLog?: WorkoutLogHeader;
  skipped: boolean;
  type: CoachProgramType;
  schedule?: UnitSchedule;
  isLastMicroCycleDay: boolean;
  processing: boolean;
  program?: CoachProgram;
  readonly isFinished: boolean;
  readonly loggedDate: dayjs.Dayjs | undefined;
  readonly completed: boolean;
  updateData(json: Partial<MicroCycleDay>): void;
  complete(workoutLog: WorkoutLog): Promise<MicroCycleDay>;
  changeParams(params?: ProgramParams): Promise<MicroCycleDay>;
  refresh(): Promise<MicroCycleDay>;
  skip(): Promise<MicroCycleDay>;
  scheduleLater(newSchedule: UnitSchedule): Promise<MicroCycleDay>;
};

export function createMicroCycleDay(json?: Partial<MicroCycleDay>): MicroCycleDay {
  const isMicroCycleDayInstance = (obj: any): obj is MicroCycleDay => 
    obj && typeof obj.updateData === 'function';

  const day: MicroCycleDay = makeAutoObservable({
    id: json?.id || '',
    athleteSuperMacroId: json?.athleteSuperMacroId || '',
    mesoCycleId: json?.mesoCycleId || '',
    microCycleId: json?.microCycleId || '',
    workoutDayTemplateId: json?.workoutDayTemplateId || '',
    params: createProgramParams(json?.params),
    coachWorkoutId: json?.coachWorkoutId ?? '',
    workout: json?.workout ? createCoachWorkout(json.workout) : undefined,
    workoutLogId: json?.workoutLogId,
    workoutLog: json?.workoutLog ? createWorkoutLogHeader(json.workoutLog) : undefined,
    skipped: json?.skipped ?? false,
    processing: false as boolean,
    type: json?.type ?? 'superMacro',
    schedule: json?.schedule ? createUnitSchedule(json.schedule) : undefined,
    isLastMicroCycleDay: json?.isLastMicroCycleDay ?? false,
    program: undefined as CoachProgram | undefined,

    get isFinished(): boolean {
      return !!(this.workoutLog && this.workoutLog.endDateTime);
    },

    get loggedDate(): dayjs.Dayjs | undefined {
      return this.workoutLog?.startDateTime ? dayjs(this.workoutLog.startDateTime) : undefined;
    },

    get completed(): boolean {
      return !!this.workoutLog || this.skipped;
    },

    updateData(json: Partial<MicroCycleDay>): void {
      this.athleteSuperMacroId = json.athleteSuperMacroId || '';
      this.mesoCycleId = json.mesoCycleId || '';
      this.microCycleId = json.microCycleId || '';
      this.workoutDayTemplateId = json.workoutDayTemplateId || '';
      this.coachWorkoutId = json.coachWorkoutId ?? '';
      this.workout = json.workout ? createCoachWorkout(json.workout) : undefined;
      this.workoutLogId = json.workoutLogId;
      this.workoutLog = json.workoutLog ? createWorkoutLogHeader(json.workoutLog) : undefined;
      this.params = createProgramParams(json.params);
      this.skipped = json.skipped ?? false;
      this.type = json.type ?? 'superMacro';
      this.schedule = json.schedule ? createUnitSchedule(json.schedule) : undefined;
      this.isLastMicroCycleDay = json.isLastMicroCycleDay ?? false;
    },

    complete(workoutLog: WorkoutLog): Promise<MicroCycleDay> {
      if (!this.workoutLogId) {
        this.workoutLogId = workoutLog.id;
        this.workoutLog = workoutLog.toHeader();

        return HttpBackend.post(
          `/coach/program/${this.athleteSuperMacroId}/cycle/${this.microCycleId}/${this.id}/complete`,
          {
            workoutLogId: workoutLog.id,
            dateTime: workoutLog.startDateTime?.toISOString(),
          },
        ).then(() => this);
      }
      return Promise.resolve(this);
    },

    changeParams(params?: ProgramParams): Promise<MicroCycleDay> {
      this.processing = true;
      this.params = params ?? this.params;
      return HttpBackend.post(
        `/coach/program/${this.athleteSuperMacroId}/cycle/${this.microCycleId}/${this.id}/changeParams`,
        this.params,
      )
        .then((result) => {
          if (result) {
            this.updateData(result);
          }
          return this;
        })
        .finally(() => runInAction(() => (this.processing = false)));
    },

    refresh(): Promise<MicroCycleDay> {
      this.processing = true;
      return HttpBackend.post(`/coach/program/${this.athleteSuperMacroId}/cycle/${this.microCycleId}/${this.id}/refresh`)
        .then((result) => {
          if (result) {
            this.updateData(result);
          }
          return this;
        })
        .finally(() => runInAction(() => (this.processing = false)));
    },

    skip(): Promise<MicroCycleDay> {
      this.processing = true;
      return HttpBackend.post(`/coach/program/${this.athleteSuperMacroId}/cycle/${this.microCycleId}/${this.id}/skip`)
        .then(() => runInAction(() => (this.skipped = true)))
        .then(() => this)
        .finally(() => runInAction(() => (this.processing = false)));
    },

    scheduleLater(newSchedule: UnitSchedule): Promise<MicroCycleDay> {
      this.processing = true;
      this.schedule = newSchedule.copy();
      return HttpBackend.post(
        `/coach/program/${this.athleteSuperMacroId}/cycle/${this.microCycleId}/${this.id}/schedule`,
        newSchedule,
      )
        .then(() => this)
        .finally(() => runInAction(() => (this.processing = false)));
    },
  });

  return day;
}

export const MicroCycleDayApi = {
  findOne(id: string): Promise<MicroCycleDay> {
    return HttpBackend.get(`/coach/program/micro-cycle-day/${id}`).then((res) => createMicroCycleDay(res));
  },
};
